import React, { useState, useEffect } from 'react';
import { getAuth } from 'firebase/auth';
import { doc, getDoc, collection, addDoc, getDocs, query, where, orderBy, deleteDoc, Timestamp } from 'firebase/firestore';
import { db } from '../components/firebase-config.js';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faQrcode, faPlus, faTrash, faDownload, faLink, faCopy, faInfoCircle,
  faEye, faCalendarAlt, faHome, faSpinner, faCheck, faTimes
} from '@fortawesome/free-solid-svg-icons';
import '../styles/QRCodeMobile.css';
import { Modal, Button, Form, Alert, Card, Row, Col, Badge, Spinner, Dropdown, ButtonGroup } from 'react-bootstrap';
import { QRCodeCanvas } from 'qrcode.react';

const QRCodeManager = ({ medecinId, affiliatedStructures, medecinData }) => {
  const [qrCodes, setQrCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedQR, setSelectedQR] = useState(null);
  const [message, setMessage] = useState(null);
  const [copySuccess, setCopySuccess] = useState(false);
  
  // Ouvre le modal de création en présélectionnant l'action souhaitée
  const openCreateModal = (actionType) => {
    setNewQRForm((prev) => ({ ...prev, action: actionType }));
    setShowCreateModal(true);
  };

  // États pour la création d'un nouveau QR code
  const [newQRForm, setNewQRForm] = useState({
    structureId: '',
    titre: '',
    description: '',
    action: 'rdv', // inscription, rdv, both
    typeConsultation: 'cabinet' // Pertinent si action != inscription
  });

  useEffect(() => {
    loadQRCodes();
  }, [medecinId]);

  const loadQRCodes = async () => {
    try {
      setLoading(true);
      const qrQuery = query(
        collection(db, 'qrCodes'),
        where('medecinId', '==', medecinId),
        orderBy('dateCreation', 'desc')
      );
      
      const snapshot = await getDocs(qrQuery);
      const qrCodesList = [];
      
      snapshot.forEach((doc) => {
        qrCodesList.push({
          id: doc.id,
          ...doc.data()
        });
      });
      
      setQrCodes(qrCodesList);
    } catch (error) {
      console.error("Erreur lors du chargement des codes QR:", error);
      setMessage({ type: 'danger', text: 'Erreur lors du chargement des codes QR' });
    } finally {
      setLoading(false);
    }
  };

  const handleCreateQR = async () => {
    try {
      if (!newQRForm.structureId || !newQRForm.titre) {
        setMessage({ type: 'danger', text: 'Veuillez remplir tous les champs obligatoires' });
        return;
      }

      const structureData = affiliatedStructures.find(s => s.id === newQRForm.structureId);
      if (!structureData) {
        setMessage({ type: 'danger', text: 'Structure introuvable' });
        return;
      }

      // Générer l'URL du QR-code
      const baseUrl = 'https://samdigitale.com/patient-portal';
      const params = new URLSearchParams();
      
      params.append('medecinId', medecinId);
      params.append('structureId', newQRForm.structureId);
      params.append('medecinTitre', medecinData?.titre || 'Dr');
      params.append('medecinNom', medecinData?.nom || '');
      params.append('medecinSpecialite', medecinData?.specialite || '');
      params.append('structureNom', structureData.nom || '');

      // Spécifier l'action souhaitée sur le portail patient
      params.append('action', newQRForm.action); // inscription, rdv, both

      // N'inclure le type de consultation que si l'action implique un RDV
      if (newQRForm.action !== 'inscription') {
        params.append('typeConsultation', newQRForm.typeConsultation);
      }

      const qrUrl = `${baseUrl}?${params.toString()}`;

      // Sauvegarder dans Firestore
      const qrCodeData = {
        medecinId,
        structureId: newQRForm.structureId,
        structureNom: structureData.nom,
        titre: newQRForm.titre,
        description: newQRForm.description,
        action: newQRForm.action,
        typeConsultation: newQRForm.action === 'inscription' ? null : newQRForm.typeConsultation,
        url: qrUrl,
        dateCreation: Timestamp.now(),
        actif: true,
        nombreUtilisations: 0
      };

      await addDoc(collection(db, 'qrCodes'), qrCodeData);
      
      setMessage({ type: 'success', text: 'Code QR créé avec succès!' });
      setShowCreateModal(false);
      setNewQRForm({ structureId: '', titre: '', description: '', action: 'rdv', typeConsultation: 'cabinet' });
      loadQRCodes();
      
    } catch (error) {
      console.error("Erreur lors de la création du QR code:", error);
      setMessage({ type: 'danger', text: 'Erreur lors de la création du code QR' });
    }
  };

  const handleDeleteQR = async (qrId) => {
    try {
      if (window.confirm('Êtes-vous sûr de vouloir supprimer ce code QR?')) {
        await deleteDoc(doc(db, 'qrCodes', qrId));
        setMessage({ type: 'success', text: 'Code QR supprimé avec succès' });
        loadQRCodes();
      }
    } catch (error) {
      console.error("Erreur lors de la suppression:", error);
      setMessage({ type: 'danger', text: 'Erreur lors de la suppression' });
    }
  };

  const downloadQRCode = (qrCode) => {
    const canvas = document.getElementById(`qr-canvas-${qrCode.id}`);
    if (canvas) {
      const pngUrl = canvas
        .toDataURL('image/png')
        .replace('image/png', 'image/octet-stream');
      
      const downloadLink = document.createElement('a');
      downloadLink.href = pngUrl;
      downloadLink.download = `qr-${qrCode.titre.replace(/\s+/g, '-').toLowerCase()}.png`;
      document.body.appendChild(downloadLink);
      downloadLink.click();
      document.body.removeChild(downloadLink);
    }
  };

  const copyToClipboard = (url) => {
    navigator.clipboard.writeText(url).then(
      () => {
        setCopySuccess(true);
        setTimeout(() => setCopySuccess(false), 2000);
      },
      (err) => {
        console.error('Erreur lors de la copie:', err);
      }
    );
  };

  const getTypeConsultationBadge = (type) => {
    switch(type) {
      case 'cabinet':
        return <Badge bg="primary" className="me-1"><FontAwesomeIcon icon={faHome} className="me-1" />Cabinet</Badge>;
      case 'domicile':
        return <Badge bg="success" className="me-1"><FontAwesomeIcon icon={faHome} className="me-1" />Domicile</Badge>;
      case 'mixte':
        return <Badge bg="info" className="me-1"><FontAwesomeIcon icon={faCalendarAlt} className="me-1" />Mixte</Badge>;
      default:
        return null;
    }
  };

  // Badge pour l'action (inscription / rdv / both)
  const getActionBadge = (action) => {
    switch(action) {
      case 'inscription':
        return <Badge bg="warning" className="me-1">Inscription</Badge>;
      case 'both':
        return <Badge bg="info" className="me-1">Inscription + RDV</Badge>;
      default:
        return <Badge bg="primary" className="me-1">RDV</Badge>;
    }
  };

  if (loading) {
    return (
      <div className="text-center py-5">
        <Spinner animation="border" variant="primary" />
        <p className="mt-2">Chargement des codes QR...</p>
      </div>
    );
  }

  return (
    <div className="qr-manager">
      {/* En-tête avec bouton de création */}
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h4 className="mb-0">
            <FontAwesomeIcon icon={faQrcode} className="me-2 text-primary" />
            Mes Codes QR
          </h4>
          <p className="text-muted mb-0">Gérez vos codes QR pour faciliter les prises de rendez-vous</p>
        </div>
        {/* Bouton déroulant pour choisir le type de QR code à créer */}
        <Dropdown as={ButtonGroup} disabled={affiliatedStructures.length === 0}>
          <Button variant="primary" onClick={() => openCreateModal('rdv')}>
            <FontAwesomeIcon icon={faPlus} className="me-2" />
            Créer un code QR
          </Button>
          <Dropdown.Toggle split variant="primary" id="dropdown-split-basic" />
          <Dropdown.Menu>
            <Dropdown.Item onClick={() => openCreateModal('inscription')}>Pour inscription</Dropdown.Item>
            <Dropdown.Item onClick={() => openCreateModal('rdv')}>Pour prise de RDV</Dropdown.Item>
            <Dropdown.Item onClick={() => openCreateModal('both')}>Inscription + RDV</Dropdown.Item>
          </Dropdown.Menu>
        </Dropdown>
      </div>

      {/* Messages */}
      {message && (
        <Alert 
          variant={message.type} 
          dismissible 
          onClose={() => setMessage(null)}
          className="mb-4"
        >
          {message.text}
        </Alert>
      )}

      {/* Affichage des codes QR */}
      {qrCodes.length === 0 ? (
        <Card className="text-center py-5">
          <Card.Body>
            <FontAwesomeIcon icon={faQrcode} size="3x" className="text-muted mb-3" />
            <h5>Aucun code QR créé</h5>
            <p className="text-muted">
              Créez votre premier code QR pour permettre à vos patients de prendre rendez-vous facilement.
            </p>
            {affiliatedStructures.length > 0 && (
              <Button variant="primary" onClick={() => openCreateModal('rdv')}>
                <FontAwesomeIcon icon={faPlus} className="me-2" />
                Créer mon premier code QR
              </Button>
            )}
          </Card.Body>
        </Card>
      ) : (
        <Row>
          {qrCodes.map((qrCode) => (
            <Col md={6} lg={4} key={qrCode.id} className="mb-4">
              <Card className="h-100 shadow-sm">
                <Card.Header className="bg-light">
                  <div className="d-flex justify-content-between align-items-center">
                    <h6 className="mb-0">{qrCode.titre}</h6>
                    <div>
                      {getActionBadge(qrCode.action)}
                      {qrCode.action !== 'inscription' && getTypeConsultationBadge(qrCode.typeConsultation)}
                    </div>
                  </div>
                </Card.Header>
                <Card.Body className="text-center">
                  <div className="mb-3">
                    <QRCodeCanvas 
                      id={`qr-canvas-${qrCode.id}`}
                      value={qrCode.url} 
                      size={150} 
                      level="H"
                      includeMargin={true}
                    />
                  </div>
                  
                  <p className="small text-muted mb-2">
                    <strong>Structure:</strong> {qrCode.structureNom}
                  </p>
                  
                  {qrCode.description && (
                    <p className="small text-muted mb-2">{qrCode.description}</p>
                  )}
                  
                  <p className="small text-muted mb-3">
                    Créé le: {qrCode.dateCreation?.toDate().toLocaleDateString()}
                  </p>
                  
                  <div className="d-flex justify-content-center gap-2">
                    <Button 
                      variant="outline-primary" 
                      size="sm"
                      onClick={() => {
                        setSelectedQR(qrCode);
                        setShowDetailModal(true);
                      }}
                    >
                      <FontAwesomeIcon icon={faEye} />
                    </Button>
                    
                    <Button 
                      variant="outline-success" 
                      size="sm"
                      onClick={() => downloadQRCode(qrCode)}
                    >
                      <FontAwesomeIcon icon={faDownload} />
                    </Button>
                    
                    <Button 
                      variant="outline-info" 
                      size="sm"
                      onClick={() => copyToClipboard(qrCode.url)}
                    >
                      <FontAwesomeIcon icon={faCopy} />
                    </Button>
                    
                    <Button 
                      variant="outline-danger" 
                      size="sm"
                      onClick={() => handleDeleteQR(qrCode.id)}
                    >
                      <FontAwesomeIcon icon={faTrash} />
                    </Button>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          ))}
        </Row>
      )}

      {copySuccess && (
        <div className="position-fixed top-0 start-50 translate-middle-x mt-3" style={{ zIndex: 9999 }}>
          <Alert variant="success" className="d-flex align-items-center">
            <FontAwesomeIcon icon={faCheck} className="me-2" />
            URL copiée dans le presse-papier!
          </Alert>
        </div>
      )}

      {/* Modal de création */}
      <Modal show={showCreateModal} onHide={() => setShowCreateModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            <FontAwesomeIcon icon={faQrcode} className="me-2" />
            Créer un nouveau code QR
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Alert variant="info">
            <FontAwesomeIcon icon={faInfoCircle} className="me-2" />
            Ce code QR permettra à vos patients de prendre rendez-vous directement avec vous pour la structure sélectionnée.
          </Alert>
          
          <Form>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Titre du code QR <span className="text-danger">(Obligatoire)</span></Form.Label>
                  <Form.Control
                    type="text"
                    value={newQRForm.titre}
                    onChange={(e) => setNewQRForm({...newQRForm, titre: e.target.value})}
                    placeholder="Ex: Consultations de cardiologie"
                  />
                </Form.Group>
              </Col>
              
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Structure ou Prestataire de santé <span className="text-danger">*</span></Form.Label>
                  <Form.Select
                    value={newQRForm.structureId}
                    onChange={(e) => setNewQRForm({...newQRForm, structureId: e.target.value})}
                  >
                    <option value="">Sélectionner une structure</option>
                    {affiliatedStructures.map(structure => (
                      <option key={structure.id} value={structure.id}>
                        {structure.nom}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>
            
            {/* Sélection du type d'action */}
            <Form.Group className="mb-3">
              <Form.Label>Fonctionnalité du QR code</Form.Label>
              <Form.Select
                value={newQRForm.action}
                onChange={(e) => setNewQRForm({...newQRForm, action: e.target.value})}
              >
                <option value="rdv">Prise de rendez-vous</option>
                <option value="inscription">Inscription patient</option>
                <option value="both">Inscription + Prise de RDV</option>
              </Form.Select>
            </Form.Group>

            {newQRForm.action !== 'inscription' && (
              <Form.Group className="mb-3">
                <Form.Label>Type de consultation</Form.Label>
                <Form.Select
                  value={newQRForm.typeConsultation}
                  onChange={(e) => setNewQRForm({...newQRForm, typeConsultation: e.target.value})}
                >
                  <option value="cabinet">Au cabinet uniquement</option>
                  <option value="domicile">À domicile uniquement</option>
                  <option value="mixte">Cabinet et domicile</option>
                </Form.Select>
              </Form.Group>
            )}
            
            <Form.Group className="mb-3">
              <Form.Label>Description (optionnelle)</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                value={newQRForm.description}
                onChange={(e) => setNewQRForm({...newQRForm, description: e.target.value})}
                placeholder="Description de ce code QR (affiché aux patients)"
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowCreateModal(false)}>
            <FontAwesomeIcon icon={faTimes} className="me-1" />
            Annuler
          </Button>
          <Button 
            variant="primary" 
            onClick={handleCreateQR}
            disabled={!newQRForm.structureId || !newQRForm.titre}
          >
            <FontAwesomeIcon icon={faQrcode} className="me-1" />
            Créer le code QR
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Modal de détails */}
      <Modal show={showDetailModal} onHide={() => setShowDetailModal(false)} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title>Détails du code QR</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedQR && (
            <div className="text-center">
              <h5 className="mb-3">{selectedQR.titre}</h5>
              
              <div className="mb-4">
                <QRCodeCanvas 
                  value={selectedQR.url} 
                  size={250} 
                  level="H"
                  includeMargin={true}
                />
              </div>
              
              <div className="text-start">
                <Row>
                  <Col md={6}>
                    <p><strong>Structure:</strong> {selectedQR.structureNom}</p>
                    <p><strong>Type de code:</strong> {getActionBadge(selectedQR.action)}</p>
                    {selectedQR.action !== 'inscription' && (
                      <p><strong>Consultation:</strong> {getTypeConsultationBadge(selectedQR.typeConsultation)}</p>
                    )}
                    <p><strong>Créé le:</strong> {selectedQR.dateCreation?.toDate().toLocaleDateString()}</p>
                  </Col>
                  <Col md={6}>
                    <p><strong>Utilisations:</strong> {selectedQR.nombreUtilisations || 0}</p>
                    <p><strong>Statut:</strong> 
                      <Badge bg={selectedQR.actif ? 'success' : 'danger'} className="ms-2">
                        {selectedQR.actif ? 'Actif' : 'Inactif'}
                      </Badge>
                    </p>
                  </Col>
                </Row>
                
                {selectedQR.description && (
                  <div className="mb-3">
                    <strong>Description:</strong>
                    <p className="text-muted">{selectedQR.description}</p>
                  </div>
                )}
                
                <div className="mb-3">
                  <strong>URL:</strong>
                  <div className="input-group mt-1">
                    <input 
                      type="text" 
                      className="form-control" 
                      value={selectedQR.url} 
                      readOnly 
                    />
                    <button 
                      className="btn btn-outline-secondary" 
                      type="button"
                      onClick={() => copyToClipboard(selectedQR.url)}
                    >
                      <FontAwesomeIcon icon={faCopy} />
                    </button>
                  </div>
                </div>
              </div>
              
              <div className="d-flex justify-content-center gap-2 mt-4">
                <Button
                  variant="primary"
                  onClick={() => downloadQRCode(selectedQR)}
                >
                  <FontAwesomeIcon icon={faDownload} className="me-1" />
                  Télécharger
                </Button>
                <Button
                  variant="outline-danger"
                  onClick={() => {
                    setShowDetailModal(false);
                    handleDeleteQR(selectedQR.id);
                  }}
                >
                  <FontAwesomeIcon icon={faTrash} className="me-1" />
                  Supprimer
                </Button>
              </div>
            </div>
          )}
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default QRCodeManager;
